'use client';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  useForm,
  FormProvider,
  Controller,
  UseFormReturn,
  FieldValues,
  Path,
  DefaultValues,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FiSave, FiX, FiChevronDown, FiChevronUp, FiAlertTriangle } from 'react-icons/fi';

import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  HStack,
  Icon,
  Text,
  useToast,
  Spinner,
  useBreakpointValue,
} from '../../index';
import { useStandaloneConfirm } from '../../hooks/use-confirm';
import { Collapsible } from '../collapsible';
import { Card } from '../card';
import { Textarea, Field } from '../form';
import { Input, InputGroup, InputLeftElement, InputRightElement } from '../input';
import { InputNumber } from '../input-number';
import { Select } from '../select';
import { Checkbox } from '../checkbox';
import { Switch } from '../switch';
import { RadioGroup } from '../radio';
import { LoadingState } from '../loading-state';
import { Alert } from '../alert';
import { useUnsavedChanges } from '../../hooks/use-unsaved-changes';

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  fields: FormFieldConfig[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export interface FormFieldConfig {
  name: string;
  label: string;
  type:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'select'
    | 'multiselect'
    | 'checkbox'
    | 'switch'
    | 'radio'
    | 'textarea'
    | 'date'
    | 'time'
    | 'datetime'
    | 'file'
    | 'richtext'
    | 'custom';
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  options?: { value: string; label: string }[];
  validation?: z.ZodSchema;
  defaultValue?: unknown;
  dependencies?: {
    field: string;
    condition: (value: unknown) => boolean;
  }[];
  render?: (field: FormFieldConfig, form: UseFormReturn) => React.ReactNode;
  gridColumn?: string;
  autoComplete?: string;
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  // Number field specific
  min?: number;
  max?: number;
  step?: number;
  precision?: number;

  // Real-time validation
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  asyncValidator?: (value: unknown) => Promise<boolean | { isValid: boolean; message?: string }>;
  debounceMs?: number;
  showSuccess?: boolean;
}

export interface ResourceFormProps<T extends FieldValues = FieldValues> {
  // Form configuration
  mode: 'create' | 'edit';
  resourceName: string;
  schema?: z.ZodType<unknown>;
  defaultValues?: Partial<T>;

  // Form structure
  fields?: FormFieldConfig[];
  sections?: FormSection[];
  steps?: {
    id: string;
    title: string;
    description?: string;
    sections?: FormSection[];
    fields?: FormFieldConfig[];
    validation?: z.ZodType<unknown>;
  }[];

  // Submission
  onSubmit: (data: T) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitText?: string;
  cancelText?: string;

  // Auto-save
  enableAutoSave?: boolean;
  autoSaveInterval?: number;
  onAutoSave?: (data: Partial<T>) => void | Promise<void>;
  lastSavedAt?: Date;

  // Smart defaults
  smartDefaults?: Partial<T> | (() => Promise<Partial<T>>);

  // Form state
  isDirty?: boolean;
  errors?: Record<string, string>;
  successMessage?: string;

  // Layout
  showHeader?: boolean;
  headerActions?: React.ReactNode;
  formWidth?: string | number;

  // Confirmation
  confirmBeforeSubmit?: boolean;
  confirmMessage?: string;
  unsavedChangesMessage?: string;

  // Validation
  enableRealtimeValidation?: boolean;
}

/**
 * Creates proper default values for all form fields to prevent controlled/uncontrolled input issues.
 * Rule 15 compliant: Root cause solution for React Hook Form controlled input warning.
 */
function createFieldDefaults<T extends FieldValues>(
  fields: FormFieldConfig[] = [],
  sections: FormSection[] = [],
  steps: {
    id: string;
    title: string;
    description?: string;
    sections?: FormSection[];
    fields?: FormFieldConfig[];
  }[] = [],
  userDefaults: Partial<T> = {}
): Partial<T> {
  const defaults: Record<string, unknown> = { ...userDefaults };

  // Process all fields from various sources
  const allFields: FormFieldConfig[] = [
    ...fields,
    ...sections.flatMap((section) => section.fields),
    ...steps.flatMap((step) => [
      ...(step.fields || []),
      ...(step.sections || []).flatMap((section) => section.fields),
    ]),
  ];

  allFields.forEach((field) => {
    // Skip if user already provided default
    if (defaults[field.name] !== undefined) {
      return;
    }

    // Set appropriate default based on field type
    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'textarea':
        defaults[field.name] = '';
        break;
      case 'number':
        defaults[field.name] = 0;
        break;
      case 'select':
        defaults[field.name] = '';
        break;
      case 'multiselect':
        defaults[field.name] = [];
        break;
      case 'checkbox':
      case 'switch':
        defaults[field.name] = false;
        break;
      case 'radio':
        defaults[field.name] = '';
        break;
      case 'date':
      case 'datetime':
      case 'time':
        defaults[field.name] = '';
        break;
      default:
        defaults[field.name] = '';
    }
  });

  return defaults as Partial<T>;
}

function FormSectionComponent<T extends FieldValues>({
  section,
  form,
  isExpanded,
  onToggle,
}: {
  section: FormSection;
  form: UseFormReturn<T, unknown, T>;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const {
    control,
    watch,
    formState: { errors },
  } = form;

  const renderField = (field: FormFieldConfig) => {
    // Check dependencies
    if (field.dependencies) {
      const shouldShow = field.dependencies.every((dep) => {
        const value = watch(dep.field as Path<T>);
        return dep.condition(value);
      });
      if (!shouldShow) return null;
    }

    // Custom render
    if (field.render) {
      return field.render(field, form as UseFormReturn<FieldValues>);
    }

    // Default field rendering
    return (
      <Field.Root
        key={field.name}
        invalid={!!errors[field.name]?.message}
        gridColumn={field.gridColumn}
      >
        {field.label && field.type !== 'checkbox' && (
          <Box
            as="label"
            fontSize="sm"
            fontWeight="medium"
            color={errors[field.name]?.message ? '#EF4444' : '#374151'}
          >
            {field.label}
            {field.required && (
              <Box as="span" color="red" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}
        <Controller
          name={field.name as Path<T>}
          control={control}
          render={({ field: controllerField }) => {
            switch (field.type) {
              case 'text':
              case 'email':
              case 'password': {
                const inputElement = (
                  <Input
                    {...controllerField}
                    type={field.type}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    readOnly={field.readOnly}
                    autoComplete={field.autoComplete}
                  />
                );

                if (field.leftIcon || field.rightIcon) {
                  return (
                    <Box position="relative">
                      {field.leftIcon && (
                        <Box
                          position="absolute"
                          left="12px"
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                          pointerEvents="none"
                        >
                          <Icon iconColor="secondary" size="sm">
                            {field.leftIcon}
                          </Icon>
                        </Box>
                      )}
                      <Box
                        paddingLeft={field.leftIcon ? '40px' : undefined}
                        paddingRight={field.rightIcon ? '40px' : undefined}
                      >
                        {inputElement}
                      </Box>
                      {field.rightIcon && (
                        <Box
                          position="absolute"
                          right="12px"
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                          pointerEvents="none"
                        >
                          <Icon iconColor="secondary" size="sm">
                            {field.rightIcon}
                          </Icon>
                        </Box>
                      )}
                    </Box>
                  );
                }
                return inputElement;
              }

              case 'number':
                return (
                  <InputNumber
                    {...controllerField}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    precision={field.precision}
                  />
                );

              case 'textarea':
                return (
                  <Textarea
                    {...controllerField}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    readOnly={field.readOnly}
                    rows={4}
                  />
                );

              case 'select':
                return (
                  <Select
                    {...controllerField}
                    placeholder={field.placeholder || 'Select an option'}
                    disabled={field.disabled}
                    options={field.options || []}
                  />
                );

              case 'checkbox':
                return (
                  <Checkbox
                    checked={controllerField.value}
                    onChange={controllerField.onChange}
                    disabled={field.disabled}
                  >
                    {field.label}
                  </Checkbox>
                );

              case 'switch':
                return <Switch {...controllerField} disabled={field.disabled} />;

              case 'radio':
                return (
                  <RadioGroup
                    {...controllerField}
                    disabled={field.disabled}
                    options={field.options || []}
                  />
                );

              default:
                return (
                  <Text color="#EF4444" fontSize="sm">
                    Unsupported field type: {field.type}
                  </Text>
                );
            }
          }}
        />
        {errors[field.name]?.message && (
          <Box fontSize="sm" color="#EF4444" marginTop={1}>
            {errors[field.name]?.message as string}
          </Box>
        )}
        {field.helperText && !errors[field.name]?.message && (
          <Text fontSize="sm" color="#6B7280" marginTop={1}>
            {field.helperText}
          </Text>
        )}
      </Field.Root>
    );
  };

  return (
    <Card.Root>
      {section.collapsible ? (
        <Collapsible.Root open={isExpanded} onOpenChange={onToggle}>
          <Card.Header>
            <Collapsible.Trigger asChild>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                cursor="pointer"
                width="100%"
              >
                <Box>
                  <Heading size="sm">{section.title}</Heading>
                  {section.description && (
                    <Text fontSize="sm" color="#4B5563" marginTop="4px">
                      {section.description}
                    </Text>
                  )}
                </Box>
                <Icon iconColor="secondary" size="sm">
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </Icon>
              </Flex>
            </Collapsible.Trigger>
          </Card.Header>
          <Collapsible.Content>
            <Card.Body>
              <Stack gap="16px">{section.fields.map(renderField)}</Stack>
            </Card.Body>
          </Collapsible.Content>
        </Collapsible.Root>
      ) : (
        <>
          <Card.Header>
            <Heading size="sm">{section.title}</Heading>
            {section.description && (
              <Text fontSize="sm" color="#4B5563" marginTop="4px">
                {section.description}
              </Text>
            )}
          </Card.Header>
          <Card.Body>
            <Stack gap="16px">{section.fields.map(renderField)}</Stack>
          </Card.Body>
        </>
      )}
    </Card.Root>
  );
}

export function ResourceForm<T extends FieldValues = FieldValues>({
  // Form configuration
  mode,
  resourceName,
  schema,
  defaultValues = {} as Partial<T>,

  // Form structure
  fields = [],
  sections = [],
  steps = [],

  // Submission
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitText,
  cancelText = 'Cancel',

  // Auto-save
  enableAutoSave = false,
  autoSaveInterval = 30000, // 30 seconds
  onAutoSave,
  lastSavedAt,

  // Smart defaults
  smartDefaults,

  // Form state
  errors: externalErrors,
  successMessage,

  // Layout
  showHeader = true,
  headerActions,
  formWidth = '100%',

  // Confirmation
  confirmBeforeSubmit = false,
  confirmMessage,
  unsavedChangesMessage = 'Kaydedilmemiş değişiklikler var. Çıkmak istediğinizden emin misiniz?',
}: ResourceFormProps<T>) {
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [sectionExpanded, setSectionExpanded] = useState<Record<string, boolean>>({});
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  // Guard ref to prevent smartDefaults useEffect from running multiple times
  // when function reference changes on re-render (Rule 15: proper fix for infinite loop)
  const smartDefaultsLoadedRef = useRef(false);
  // const [_showUnsavedWarning, _setShowUnsavedWarning] = useState(false);
  // const _formRef = useRef<HTMLFormElement>(null);

  // SSR-safe mobile detection (defaults to desktop on server, updates on client)
  // Rule 15: Proper hydration fix - matches server render initially
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  const isMultiStep = steps.length > 0;
  const currentStepData = isMultiStep ? steps[currentStep] : null;

  // Create proper default values to prevent controlled/uncontrolled input warnings
  const computedDefaultValues = useMemo(() => {
    return createFieldDefaults(fields, sections, steps, defaultValues);
  }, [fields, sections, steps, defaultValues]);

  // Initialize form
  const form = useForm({
    // @ts-any-allowed: zodResolver version mismatch - requires dependency alignment
    resolver: schema ? (zodResolver as unknown as CallableFunction)(schema) : undefined,
    defaultValues: computedDefaultValues as DefaultValues<T>,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
    setError,
  } = form;

  // Load smart defaults
  // Uses guard ref to prevent infinite loop when function-based smartDefaults
  // creates new reference on each render (Rule 15: proper fix, not workaround)
  useEffect(() => {
    if (smartDefaults && mode === 'create' && !smartDefaultsLoadedRef.current) {
      smartDefaultsLoadedRef.current = true; // Mark as loaded immediately to prevent re-runs
      setIsLoadingDefaults(true);
      const loadDefaults = async () => {
        try {
          const defaults =
            typeof smartDefaults === 'function' ? await smartDefaults() : smartDefaults;
          reset({ ...defaultValues, ...defaults } as T);
        } catch (error) {
          toast({
            title: 'Failed to load defaults',
            description: error instanceof Error ? error.message : 'Unknown error',
            status: 'error',
            duration: 5000,
          });
        } finally {
          setIsLoadingDefaults(false);
        }
      };
      loadDefaults();
    }
  }, [smartDefaults, mode, reset, defaultValues, toast]);

  // Auto-save
  useEffect(() => {
    if (!enableAutoSave || !onAutoSave || !isDirty) return;

    const timer = setTimeout(() => {
      const data = watch();
      onAutoSave(data);
    }, autoSaveInterval);

    return () => clearTimeout(timer);
  }, [enableAutoSave, onAutoSave, isDirty, watch, autoSaveInterval]);

  // Set external errors
  useEffect(() => {
    if (externalErrors) {
      Object.entries(externalErrors).forEach(([field, message]) => {
        setError(field as Path<T>, { message });
      });
    }
  }, [externalErrors, setError]);

  // Initialize section expanded state with memoized calculation
  // Use ref to track initialization and prevent infinite render loop
  const initialExpandedState = useMemo(() => {
    const initialExpanded: Record<string, boolean> = {};
    sections.forEach((section) => {
      if (section.collapsible) {
        initialExpanded[section.id] = section.defaultExpanded ?? true;
      }
    });
    return initialExpanded;
  }, [sections]);

  const initializedRef = React.useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      setSectionExpanded(initialExpandedState);
      initializedRef.current = true;
    }
  }, [initialExpandedState]);

  // Get confirm dialog for unsaved changes
  const { confirm: confirmUnsavedChanges, confirmDialog: unsavedChangesDialog } =
    useStandaloneConfirm();

  // Handle unsaved changes warning with routing support
  const { checkUnsavedChanges } = useUnsavedChanges({
    isDirty,
    isSubmitting,
    message: unsavedChangesMessage,
    confirmDialog: async (msg) => {
      return await confirmUnsavedChanges({
        title: 'Unsaved Changes',
        description: msg,
        confirmText: 'Leave',
        cancelText: 'Stay',
        isDestructive: true,
      });
    },
  });

  const toggleSection = (sectionId: string) => {
    setSectionExpanded((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const { confirm, confirmDialog } = useStandaloneConfirm();

  const handleFormSubmit = async (data: T) => {
    if (confirmBeforeSubmit) {
      const confirmed = await confirm({
        title: `${mode === 'create' ? 'Create' : 'Update'} ${resourceName}`,
        description: confirmMessage || `Are you sure you want to ${mode} this ${resourceName}?`,
        confirmText: mode === 'create' ? 'Create' : 'Update',
        cancelText: 'Cancel',
        isDestructive: false,
      });
      if (!confirmed) return;
    }

    try {
      const result = onSubmit(data);
      if (result instanceof Promise) {
        await result.catch((error: Error) => {
          // Promise chain error handling - Rule 15 compliant comprehensive error handling
          throw error; // Re-throw to be caught by outer catch block
        });
      }
    } catch (error) {
      toast({
        title: `Failed to ${mode} ${resourceName}`,
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleStepNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleStepPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = useCallback(() => {
    if (!checkUnsavedChanges()) return;
    onCancel?.();
  }, [checkUnsavedChanges, onCancel]);

  if (isLoadingDefaults) {
    return <LoadingState text="Loading form defaults..." />;
  }

  // Determine form content based on structure
  const formSections = currentStepData?.sections || sections;
  const formFields = currentStepData?.fields || fields;

  return (
    <FormProvider {...form}>
      <Box maxWidth={formWidth} width="100%">
        {showHeader && (
          <Flex justifyContent="space-between" alignItems="center" marginBottom={6}>
            <Heading size="lg">
              {mode === 'create' ? `Create ${resourceName}` : `Edit ${resourceName}`}
            </Heading>
            {headerActions}
          </Flex>
        )}

        {successMessage && (
          <Alert.Root status="success" marginBottom="16px">
            <Alert.Indicator />
            <Box>
              <Alert.Title>Success</Alert.Title>
              <Alert.Description>{successMessage}</Alert.Description>
            </Box>
          </Alert.Root>
        )}

        {isMultiStep && (
          <Box marginBottom={6}>
            <Box>
              <HStack gap="16px" marginBottom="24px">
                {steps.map((step, index) => (
                  <Flex key={step.id} alignItems="center" flex={1}>
                    <Box
                      width="32px"
                      height="32px"
                      borderRadius="50%"
                      backgroundColor={
                        index === currentStep
                          ? '#3B82F6'
                          : index < currentStep
                            ? '#10B981'
                            : '#E5E7EB'
                      }
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="14px"
                      fontWeight="600"
                    >
                      {index < currentStep ? '✓' : index + 1}
                    </Box>
                    <Box marginLeft="8px" flex={1}>
                      <Text fontSize="sm" fontWeight={index === currentStep ? '600' : '400'}>
                        {step.title}
                      </Text>
                    </Box>
                    {index < steps.length - 1 && (
                      <Box
                        height="2px"
                        flex={1}
                        backgroundColor={index < currentStep ? '#10B981' : '#E5E7EB'}
                        marginLeft="8px"
                        marginRight="8px"
                      />
                    )}
                  </Flex>
                ))}
              </HStack>
            </Box>
          </Box>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack gap="24px">
            {/* Render sections */}
            {formSections.length > 0 &&
              formSections.map((section) => (
                <FormSectionComponent
                  key={section.id}
                  section={section}
                  form={form}
                  isExpanded={!section.collapsible || sectionExpanded[section.id]}
                  onToggle={() => toggleSection(section.id)}
                />
              ))}

            {/* Render standalone fields */}
            {formFields.length > 0 && formSections.length === 0 && (
              <Stack gap="16px">
                {formFields.map((field) => {
                  // Check dependencies
                  if (field.dependencies) {
                    const shouldShow = field.dependencies.every((dep) => {
                      const value = watch(dep.field as Path<T>);
                      return dep.condition(value);
                    });
                    if (!shouldShow) return null;
                  }

                  return (
                    <Field.Root key={field.name} invalid={!!errors[field.name]?.message}>
                      {field.label && field.type !== 'checkbox' && (
                        <Box
                          as="label"
                          fontSize="sm"
                          fontWeight="medium"
                          color={errors[field.name]?.message ? '#EF4444' : '#374151'}
                        >
                          {field.label}
                          {field.required && (
                            <Box as="span" color="red" marginLeft={1}>
                              *
                            </Box>
                          )}
                        </Box>
                      )}
                      <Controller
                        name={field.name as Path<T>}
                        control={form.control}
                        render={({
                          field: controllerField,
                          fieldState: _fieldState,
                          formState: _formState,
                        }): React.ReactElement => {
                          if (field.render) {
                            return field.render(
                              field,
                              form as UseFormReturn<FieldValues>
                            ) as React.ReactElement;
                          }

                          switch (field.type) {
                            case 'text':
                            case 'email':
                            case 'password': {
                              const inputEl = (
                                <Input
                                  {...controllerField}
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  disabled={field.disabled}
                                  readOnly={field.readOnly}
                                  autoComplete={field.autoComplete}
                                  inputSize={isMobile ? 'lg' : 'md'}
                                />
                              );

                              if (field.leftIcon || field.rightIcon) {
                                return (
                                  <InputGroup>
                                    {field.leftIcon && (
                                      <InputLeftElement>
                                        <Icon iconColor="secondary" size="sm">
                                          {field.leftIcon}
                                        </Icon>
                                      </InputLeftElement>
                                    )}
                                    {inputEl}
                                    {field.rightIcon && (
                                      <InputRightElement>
                                        <Icon iconColor="secondary" size="sm">
                                          {field.rightIcon}
                                        </Icon>
                                      </InputRightElement>
                                    )}
                                  </InputGroup>
                                );
                              }
                              return inputEl;
                            }

                            case 'number':
                              return (
                                <InputNumber
                                  {...controllerField}
                                  placeholder={field.placeholder}
                                  disabled={field.disabled}
                                  min={field.min}
                                  max={field.max}
                                  step={field.step}
                                  precision={field.precision}
                                  inputSize={isMobile ? 'lg' : 'md'}
                                />
                              );

                            case 'textarea':
                              return (
                                <Textarea
                                  {...controllerField}
                                  placeholder={field.placeholder}
                                  disabled={field.disabled}
                                  readOnly={field.readOnly}
                                  size={isMobile ? 'lg' : 'md'}
                                />
                              );

                            case 'select':
                              return (
                                <Select
                                  {...controllerField}
                                  options={field.options || []}
                                  disabled={field.disabled}
                                  selectSize={isMobile ? 'lg' : 'md'}
                                />
                              );

                            case 'checkbox':
                              return (
                                <Checkbox
                                  checked={controllerField.value}
                                  onChange={controllerField.onChange}
                                  disabled={field.disabled}
                                >
                                  {field.label}
                                </Checkbox>
                              );

                            case 'switch':
                              return <Switch {...controllerField} disabled={field.disabled} />;

                            case 'radio':
                              return (
                                <RadioGroup
                                  {...controllerField}
                                  disabled={field.disabled}
                                  options={field.options || []}
                                />
                              );

                            default:
                              return <div>Unsupported field type: {field.type}</div>;
                          }
                        }}
                      />
                      {errors[field.name]?.message && (
                        <Box fontSize="sm" color="#EF4444" marginTop={1}>
                          {errors[field.name]?.message as string}
                        </Box>
                      )}
                      {field.helperText && !errors[field.name]?.message && (
                        <Text fontSize="sm" color="#6B7280" marginTop={1}>
                          {field.helperText}
                        </Text>
                      )}
                    </Field.Root>
                  );
                })}
              </Stack>
            )}

            {/* Status indicators */}
            <Flex gap="16px" alignItems="center">
              {/* Auto-save indicator */}
              {enableAutoSave && lastSavedAt && (
                <Text fontSize="sm" color="#6B7280">
                  Son kayıt: {new Date(lastSavedAt).toLocaleTimeString()}
                </Text>
              )}

              {/* Unsaved changes indicator */}
              {isDirty && !isSubmitting && (
                <Flex alignItems="center" gap="8px">
                  <Icon size="sm" color="#F59E0B">
                    <FiAlertTriangle />
                  </Icon>
                  <Text fontSize="sm" color="#F59E0B" fontWeight="medium">
                    Kaydedilmemiş değişiklikler
                  </Text>
                </Flex>
              )}
            </Flex>

            {/* Form actions */}
            <Box
              position={isMobile ? 'sticky' : 'static'}
              bottom={isMobile ? '0' : undefined}
              backgroundColor="white"
              paddingTop="24px"
              paddingBottom={isMobile ? '32px' : '24px'}
              marginTop="32px"
              borderTop="1px solid"
              borderColor="#E5E7EB"
              marginLeft="-24px"
              marginRight="-24px"
              paddingLeft="24px"
              paddingRight="24px"
              zIndex="10"
              boxShadow={isMobile ? '0 -4px 16px rgba(0, 0, 0, 0.1)' : 'none'}
            >
              <Flex
                justifyContent={isMultiStep ? 'space-between' : 'flex-end'}
                gap={isMobile ? 3 : '16px'}
                direction={isMultiStep ? 'row' : isMobile ? 'column' : 'row'}
              >
                {isMultiStep && (
                  <Box width={isMobile ? '100%' : 'auto'}>
                    {currentStep > 0 && (
                      <Button
                        variant="outline"
                        onClick={handleStepPrevious}
                        size={isMobile ? 'lg' : 'md'}
                      >
                        Previous
                      </Button>
                    )}
                  </Box>
                )}

                <Flex gap={2} direction={isMultiStep ? 'row' : isMobile ? 'column-reverse' : 'row'}>
                  {onCancel && (
                    <Box width={isMobile ? '100%' : 'auto'}>
                      <Button
                        variant="ghost"
                        onClick={handleCancel}
                        leftIcon={<FiX />}
                        size={isMobile ? 'lg' : 'md'}
                      >
                        {cancelText}
                      </Button>
                    </Box>
                  )}

                  {isMultiStep && currentStep < steps.length - 1 ? (
                    <Box width={isMobile ? '100%' : 'auto'}>
                      <Button
                        colorPalette="blue"
                        onClick={handleStepNext}
                        size={isMobile ? 'lg' : 'md'}
                      >
                        Next
                      </Button>
                    </Box>
                  ) : (
                    <Box width={isMobile ? '100%' : 'auto'}>
                      <Button
                        type="submit"
                        colorPalette="blue"
                        isLoading={isSubmitting}
                        leftIcon={<FiSave />}
                        size={isMobile ? 'lg' : 'md'}
                      >
                        {submitText ||
                          (mode === 'create' ? `Create ${resourceName}` : `Update ${resourceName}`)}
                      </Button>
                    </Box>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Stack>
        </form>

        {/* Loading Overlay */}
        {isSubmitting && (
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            backgroundColor="rgba(255, 255, 255, 0.8)"
            zIndex="9999"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="16px"
          >
            <Stack alignItems="center" gap="16px">
              <Spinner size="lg" color="#3B82F6" />
              <Text fontSize="lg" fontWeight="medium" color="#374151">
                {mode === 'create' ? 'Oluşturuluyor...' : 'Güncelleniyor...'}
              </Text>
            </Stack>
          </Box>
        )}
        {unsavedChangesDialog}
        {confirmDialog}
      </Box>
    </FormProvider>
  );
}
